import { ENV_VARS } from "@/constants/env";
import i18n from "@/i18n";
import { languageHeader } from "@/i18n/request-language";
import { getAuthTokensFromLocalStorage } from "@/utils/auth";

export interface GenerateCallbacks<TFinal = unknown> {
  /** A pipeline stage started (step = intent|connectors|workflow|run|ui|preview). */
  onStatus: (step: string, label: string) => void;
  /** Debug snapshot of a completed phase's JSON. */
  onSnapshot?: (step: string, payload: unknown) => void;
  /** Terminal success/refusal payload (check result.success). */
  onFinal: (result: TFinal) => void;
  /** Terminal pipeline failure. */
  onError: (error: Error) => void;
  /** Always fires last. */
  onDone: () => void;
}

const STREAM_IDLE_TIMEOUT_MS = 180_000;

export const apiUrl = (path: string) =>
  `${ENV_VARS.API_INSTANCE.replace(/\/$/, "")}${path}`;

export const authHeaders = (): Record<string, string> => {
  const authTokens = getAuthTokensFromLocalStorage();
  return {
    ...(authTokens ? { Authorization: `Bearer ${authTokens.accessToken}` } : {}),
    ...languageHeader(),
  };
};

/**
 * Consume a My App generation SSE stream (mirrors useStreamingChat's fetch +
 * ReadableStream frame parser, tuned to the engine's event contract: status /
 * snapshot / final / error / done). Generation can run for a while (a real
 * workflow test run happens mid-pipeline), hence the long idle timeout.
 *
 * Plain function, not a hook, so a background job can keep reading after the
 * page that started it unmounts. Aborting `controller` ends it quietly.
 *
 * `onDisconnect`, when given, is called instead of `onError` if the connection
 * drops before the run ended (network blip, sleep) — a server-side job is
 * still running then, so the caller re-attaches rather than failing it. An
 * HTTP error response is always a real error.
 */
export const consumeSse = async <TFinal = unknown>(
  url: string,
  init: RequestInit,
  callbacks: GenerateCallbacks<TFinal>,
  controller: AbortController,
  onDisconnect?: () => void,
): Promise<void> => {
  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  let errorDelivered = false;
  let finalDelivered = false;
  let doneDelivered = false;

  const deliverError = (err: Error) => {
    if (errorDelivered) return;
    errorDelivered = true;
    callbacks.onError(err);
  };

  const resetIdleTimer = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      deliverError(new Error(t("common:generation_jobs.timed_out")));
      controller.abort();
    }, STREAM_IDLE_TIMEOUT_MS);
  };

  try {
    const response = await fetch(url, { ...init, signal: controller.signal });

    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as Record<
        string,
        unknown
      >;
      throw Object.assign(
        new Error((err?.message as string) ?? `HTTP ${response.status}`),
        { http: true },
      );
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    resetIdleTimer();

    while (true) {
      if (controller.signal.aborted) {
        await reader.cancel().catch(() => {});
        break;
      }
      const { done, value } = await reader.read();
      if (done) break;
      resetIdleTimer();
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        if (controller.signal.aborted) break;
        if (!part.trim()) continue;
        let eventType = "";
        let dataStr = "";
        for (const line of part.split("\n")) {
          if (line.startsWith("event:")) eventType = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr = line.slice(5).trim();
        }
        if (!eventType || !dataStr) continue;
        let data: Record<string, unknown>;
        try {
          data = JSON.parse(dataStr);
        } catch {
          continue; // malformed frame — skip
        }

        if (eventType === "status") {
          callbacks.onStatus(String(data.step ?? ""), String(data.status ?? ""));
        } else if (eventType === "snapshot") {
          callbacks.onSnapshot?.(String(data.step ?? ""), data.payload);
        } else if (eventType === "final") {
          finalDelivered = true;
          callbacks.onFinal(data as unknown as TFinal);
        } else if (eventType === "error") {
          deliverError(
            new Error(
              (data?.message as string) ??
                (data?.error as string) ??
                t("common:generation_jobs.failed"),
            ),
          );
        } else if (eventType === "done") {
          doneDelivered = true;
          callbacks.onDone();
        }
      }
    }

    if (!finalDelivered && !doneDelivered && !controller.signal.aborted) {
      if (onDisconnect && !errorDelivered) return onDisconnect();
      deliverError(new Error(t("common:generation_jobs.connection_lost")));
    }
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
    if (onDisconnect && !(err as { http?: boolean }).http) onDisconnect();
    else deliverError(err as Error);
  } finally {
    if (idleTimer) clearTimeout(idleTimer);
  }
};

// ── Server-side generation jobs ───────────────────────────────────────────────
// Every AI builder that runs as an API generation job (My Apps, Studio
// workflows / email / document templates) talks to the same four routes under
// its own `…/jobs` path: start (module-specific POST), list, stream, remove.

const t = (key: string, opts?: Record<string, unknown>) =>
  i18n.t(key, opts) as string;

/** The caller's jobs on a module's `…/jobs` route; throws when unreachable. */
export const fetchJobs = async <TJob>(jobsPath: string): Promise<TJob[]> => {
  const res = await fetch(apiUrl(jobsPath), { headers: authHeaders() });
  if (!res.ok) throw new Error(String(res.status));
  return ((await res.json()) as { data?: TJob[] }).data ?? [];
};

/** Start a job; resolves to its id, or throws the API's (translated) message. */
export const postJob = async (path: string, body: unknown): Promise<string> => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as {
    message?: string;
    data?: { jobId?: string };
  };
  if (!res.ok || !json.data?.jobId) {
    throw new Error(json.message ?? `HTTP ${res.status}`);
  }
  return json.data.jobId;
};

// Jobs already removed this page load, so a later cancel doesn't DELETE again.
const removed = new Set<string>();

/**
 * Stop / forget a job server-side. Best effort: it expires within the hour
 * anyway. `jobsPath` is the module's jobs route (`…/jobs`).
 */
export const removeServerJob = (jobsPath: string, jobId: string | null) => {
  if (!jobId || removed.has(jobId)) return;
  removed.add(jobId);
  void fetch(apiUrl(`${jobsPath}/${jobId}`), {
    method: "DELETE",
    headers: authHeaders(),
  }).catch(() => {});
};

const MAX_RECONNECTS = 5;

/**
 * Follow a job's frames. A dropped connection is not a failed build — the job
 * is still running server-side — so re-attach from the last frame received,
 * backing off a little each time, and only give up after several tries.
 */
export const followJob = <TFinal = unknown>(
  jobsPath: string,
  jobId: string,
  callbacks: GenerateCallbacks<TFinal>,
  controller: AbortController,
) => {
  let received = 0;
  let attempts = 0;
  // A replay after a reconnect could re-send a frame we skipped counting, so
  // the run's outcome is delivered once no matter what.
  let ended = false;
  const counted: GenerateCallbacks<TFinal> = {
    onStatus: (...a) => {
      received++;
      callbacks.onStatus(...a);
    },
    onSnapshot: (...a) => {
      received++;
      callbacks.onSnapshot?.(...a);
    },
    onFinal: (...a) => {
      received++;
      if (ended) return;
      ended = true;
      callbacks.onFinal(...a);
    },
    onError: (err) => {
      received++;
      if (ended) return;
      ended = true;
      callbacks.onError(err);
    },
    onDone: () => {
      received++;
      callbacks.onDone();
    },
  };

  const attach = () =>
    void consumeSse(
      apiUrl(`${jobsPath}/${jobId}/stream?from=${received}`),
      { method: "GET", headers: authHeaders() },
      counted,
      controller,
      () => {
        if (controller.signal.aborted || ended) return;
        if (++attempts > MAX_RECONNECTS) {
          ended = true;
          callbacks.onError(
            new Error(t("common:generation_jobs.connection_lost")),
          );
          return;
        }
        setTimeout(attach, 1000 * attempts);
      },
    );

  attach();
};
